/**
 * IMBEE-9563 — when the channel's configured greeting should be shown.
 *
 * Extracted from the stomp connectCallback so the decision can be tested. It lived inline,
 * which meant the only way to exercise it was a live broker; this repo's Test job is blocking,
 * so the four cases below now gate permanently.
 *
 * The product answer this encodes (IMBEE-9563 AC-1): show the greeting whenever a conversation
 * opens with NO messages — not once per visitor. That covers a new visitor and a returning one
 * whose history is empty. The consequence worth knowing is that a returning visitor whose
 * history was cleared is greeted again.
 */
export type WelcomeMessageDecision = {
    /** Messages just loaded from the server. 0 = empty conversation, undefined = fetch failed. */
    loadedCount: number | undefined;
    /** What the message list holds right now, read at the moment of writing. */
    existingMessageCount: number;
    /** The channel's configured greeting; empty when the channel has none. */
    welcomeMessage: string;
};

export const shouldShowWelcomeMessage = ({
    loadedCount,
    existingMessageCount,
    welcomeMessage,
}: WelcomeMessageDecision): boolean => {
    // undefined means the fetch FAILED, which is not an empty conversation. Painting a greeting
    // over history we merely could not load would misrepresent the conversation to the visitor.
    if (loadedCount !== 0) return false;

    // A channel with no greeting configured shows nothing rather than an empty bubble.
    if (!welcomeMessage) return false;

    // The live list, not just the fetch result: the append happens after an await, so two
    // overlapping connects could both see loadedCount === 0. Also covers a message arriving in
    // that gap. Defensive — the interleaving is reachable by inspection, not observed.
    if (existingMessageCount > 0) return false;

    return true;
};

/**
 * IMBEE-8955 (T115) — the greeting's state after a connect, as ONE answer instead of two.
 *
 * `connectCallback` calls dropMessages() and then rebuilds the thread, so both questions are
 * settled by the same decision: whether to ADD the greeting, and whether the greeting is
 * consequently IN the thread. They were two separate statements before, and the second one was
 * a latch -- set true when the greeting was added and never set back to false.
 *
 * That latch was wrong across a RECONNECT, which is an ordinary event here: errorCallback calls
 * loadSession(), which sets a new stompUrl, which re-runs the effect and reaches this code
 * again. A visitor who had already sent a message reloads with loadedCount > 0, so no greeting
 * is re-added -- but the stale flag still told Conversation the in-hours response-time line had
 * a greeting to sit under, and Messages slots that line beneath messages[0] unconditionally. It
 * would render under the visitor's own oldest message: the same defect 94a7d7c fixed, reached
 * by a different route.
 *
 * Returning both from one place is the fix, not a tidy-up. Two fields that must agree cannot
 * drift when nothing can set them apart.
 */
export const greetingStateOnConnect = (
    decision: WelcomeMessageDecision,
): { addGreeting: boolean; greetingInThread: boolean } => {
    const addGreeting = shouldShowWelcomeMessage(decision);
    return { addGreeting, greetingInThread: addGreeting };
};
